/* eslint-disable max-lines,max-statements */
import {AxiosPromise} from 'axios';
import FormData from 'form-data';

import {ActionType, EmptyActionType} from '@typescript/common/actionTypes';
import {AddressType, PaymentType, ShippingMethodType} from '@typescript/paymentTypes';
import {CHECKOUT} from 'constants/actions';
import {ENDPOINTS} from 'constants/endpoints';
import {HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'constants/api';
import {getAffId} from 'libs/analytics/affiliate';
import {instanceGusaBackend} from 'libs/api';

import {getGaClientId} from 'libs/analytics/refactoringAnalytics';
import {PAYMENT_METHODS} from 'constants/checkout/paymentMethods';
import {PAYMENTS_METHODS} from 'features/paymentsMethods/constants';

/**
 * Get list shipping method by country and region
 * @param {string} country - Country id
 * @returns {ActionType<AxiosPromise>} Action
 */
export function fetchShippingMethods(country = 'US'): ActionType<AxiosPromise> {
    return {
        type: CHECKOUT.GET_SHIPPING_METHODS,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.CHECKOUT_GET_SHIPPING}?country=${country}`,
        }),
    };
}

/**
 * Tells us that save order was successful
 * @returns {EmptyActionType} - action
 */
export const saveOrderSuccess = (): EmptyActionType => ({
    type: CHECKOUT.SAVE_ORDER_SUCCESS,
});

/**
 * Reset order errors
 * @returns {EmptyActionType} - action
 */
export const resetOrderErrors = (): EmptyActionType => ({
    type: CHECKOUT.RESET_ORDER_ERRORS,
});

/**
 * Initiate a process of saving payment method on backend and instant purchase
 * @param {ActionType} payload - action payload
 * @returns {EmptyActionType} - action
 */
export function submitInstantPaymentMethod(payload): ActionType {
    return {
        type: CHECKOUT.SUBMIT_INSTANT_PAYMENT_METHOD,
        payload,
    };
}

/**
 * Place order
 * @param {boolean} subscribe - subscribe newsletter
 * @returns {ActionType<boolean>} - action
 */
export const placeOrder = (subscribe: boolean): ActionType<boolean> => ({
    type: CHECKOUT.PLACE_ORDER,
    payload: subscribe,
});

/**
 * Set shipping method error
 * @param {string} error - Shipping method error
 * @returns {ActionType<string>} - action
 */
export const setShippingMethodError = (error: string): ActionType<string> => ({
    type: CHECKOUT.SET_SHIPPING_METHOD_ERROR,
    payload: error,
});

/**
 * Set shipping method
 * @param {Array<ShippingMethodType>} list - List of shipping methods
 * @returns {ActionType<Array<ShippingMethodType>>} Action
 */
export function setShippingMethods(
    list: Array<ShippingMethodType>,
): ActionType<Array<ShippingMethodType>> {
    return {
        type: CHECKOUT.SET_SHIPPING_METHODS,
        payload: list,
    };
}

/**
 * Get list shipping method by country and region
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getPaymentMethods(): ActionType<AxiosPromise> {
    return {
        type: 'GET_PAYMENT_METHODS',
        payload: instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.CHECKOUT_GET_PAYMENTS,
        }),
    };
}

/**
 * Set active payment method
 * @param {PaymentType} payment payment info
 * @returns {ActionType<PaymentType>} - action
 */
export function setActivePayment(payment: PaymentType): ActionType<PaymentType> {
    return {
        type: CHECKOUT.SET_ACTIVE_PAYMENT,
        payload: payment,
    };
}

/**
 * Initiate a process of saving payment method on backend and going to order review step
 * @returns {EmptyActionType} - action
 */
export function submitPaymentMethod(): EmptyActionType {
    return {
        type: CHECKOUT.SUBMIT_PAYMENT_METHOD,
    };
}

/**
 * Initiate a process of settong payment method
 * @param {PaymentType} payment method info
 * @returns {ActionType<PaymentType>} - action
 */
export function choosePaymentMethod(payment: PaymentType): ActionType<PaymentType> {
    return {
        type: CHECKOUT.CHOOSE_PAYMENT_METHOD,
        payload: payment,
    };
}

/**
 * Get list shipping method by country and region
 * @param {ShippingMethodType} shippingActive - Current active shipping
 * @returns {{type: string, shippingActive: {}}} Action
 */
export function setActiveShipping(
    shippingActive: ShippingMethodType,
): {type: string; shippingActive: {}} {
    return {
        type: 'SET_ACTIVE_SHIPPING',
        shippingActive,
    };
}

/**
 * Save Addresses data
 * @param {{billingAddress: AddressType, shippingAddress: AddressType}} addressInfo - contains addresses info
 * @param {string} email Customer email
 * @param {string} formKey - form key
 * @returns {ActionType<AxiosPromise>} - Action
 */
export function saveAddresses(
    addressInfo: {billingAddress: AddressType; shippingAddress: AddressType},
    email: string,
    formKey: string,
): ActionType<AxiosPromise> {
    const {shippingAddress, billingAddress} = addressInfo;
    const data = new FormData();
    const affid = getAffId();

    data.append('billing[firstname]', billingAddress.firstName);
    data.append('billing[lastname]', billingAddress.lastName);
    data.append('billing[email]', email);
    data.append('billing[street][]', billingAddress.street);
    data.append('billing[street][]', billingAddress.aptSuiteOther);
    data.append('billing[city]', billingAddress.city);
    data.append('billing[region_id]', billingAddress.regionId);
    data.append('billing[region]', billingAddress.region);
    data.append('billing[postcode]', billingAddress.zipCode);
    data.append('billing[country_id]', billingAddress.country);
    data.append('billing[telephone]', billingAddress.phone);

    data.append('shipping[firstname]', shippingAddress.firstName);
    data.append('shipping[lastname]', shippingAddress.lastName);
    data.append('shipping[email]', email);
    data.append('shipping[street][]', shippingAddress.street);
    data.append('shipping[street][]', shippingAddress.aptSuiteOther);
    data.append('shipping[city]', shippingAddress.city);
    data.append('shipping[region_id]', shippingAddress.regionId);
    data.append('shipping[region]', shippingAddress.region);
    data.append('shipping[postcode]', shippingAddress.zipCode);
    data.append('shipping[country_id]', shippingAddress.country);
    data.append('shipping[telephone]', shippingAddress.phone);

    data.append('affid', affid);

    data.append('form_key', formKey);

    return {
        type: 'SAVE_ADDRESSES_INFO',
        payload: instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.ADDRESSES_SAVE,
            data,
        }),
    };
}

/**
 * Save shipping method
 * @param {ShippingMethodType} shippingMethod active shipping method
 * @returns {ActionType<AxiosPromise>} Action
 */
export function saveShippingMethod(shippingMethod: ShippingMethodType): ActionType<AxiosPromise> {
    const data = new FormData();
    data.append('shipping_method', shippingMethod.code);

    return {
        type: CHECKOUT.SAVE_SHIPPING_METHOD,
        payload: instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.CHECKOUT_SET_SHIPPING,
            data,
        }),
    };
}

/**
 * Get checkout totals
 * @return {ActionType<AxiosPromise>} Action
 */
export function getTotals(): ActionType<AxiosPromise> {
    return {
        type: 'CHECKOUT_GET_TOTALS',
        payload: instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.CHECKOUT_GET_TOTALS_V2,
            params: {collectTax: 1},
        }),
    };
}

/**
 * Fetch a customer addresses
 * @return {ActionType<AxiosPromise>} Action
 */
export function fetchCustomerAddresses(): ActionType<AxiosPromise> {
    return {
        type: 'FETCH_CUSTOMER_ADDRESSES',
        payload: instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.ADDRESSES_GET,
        }),
    };
}

/**
 * Remove customer address
 * @param {string} id - address id
 * @return {ActionType<AxiosPromise>} Action
 */
export function removeCustomerAddress(id: string): ActionType<AxiosPromise> {
    return {
        type: 'REMOVE_CUSTOMER_ADDRESS',
        payload: instanceGusaBackend({
            method: 'post',
            url: `${ENDPOINTS.ADDRESSES_REMOVE}?id=${id}`,
        }),
    };
}

type ReturnSetAddressDataType = {
    address: AddressType;
    index: number;
};

/**
 * Set shipping address
 * @param {AddressType} address Input value
 * @param {number} index Address array index
 * @return {{type: string, data: ReturnSetAddressDataType}} Action
 */
export function setCurrentShippingAddress(
    address: AddressType,
    index: number,
): {type: string; data: ReturnSetAddressDataType} {
    return {
        type: 'SET_CURRENT_SHIPPING_ADDRESS',
        data: {address, index},
    };
}

/**
 * Set shipping address
 * @param {AddressType} address Input value
 * @param {number} index Address array index
 * @return {{type: string, data: ReturnSetAddressDataType}} Action
 */
export function setCurrentBillingAddress(
    address: AddressType,
    index: number,
): {type: string; data: ReturnSetAddressDataType} {
    return {
        type: 'SET_CURRENT_BILLING_ADDRESS',
        data: {address, index},
    };
}

type ReturnEditAddressDataType = {
    value: string;
    name: string;
};

/**
 * Edit current shipping address
 * @param {string} value Current value
 * @param {string} name Current name
 * @return {{type: string, data: ReturnEditAddressDataType}} Return new shipping address
 */
export function editCurrentShippingAddress(
    value: string,
    name: string,
): {type: string; data: ReturnEditAddressDataType} {
    return {
        type: 'EDIT_CURRENT_SHIPPING_ADDRESS',
        data: {value, name},
    };
}

/**
 * Edit current billing address
 * @param {string} value Current value
 * @param {string} name Current name
 * @return {{type: string, data: ReturnEditAddressDataType}} Return new billing address
 */
export function editCurrentBillingAddress(
    value: string,
    name: string,
): {type: string; data: ReturnEditAddressDataType} {
    return {
        type: 'EDIT_CURRENT_BILLING_ADDRESS',
        data: {value, name},
    };
}

type ReturnChangeAddressDataType = {
    editable: boolean;
};

/**
 * Change current shipping address mode
 * @param {boolean} editable Editable
 * @return {{type: string, data: ReturnChangeAddressDataType}} New address mode
 */
export function changeCurrentShippingAddressMode(
    editable: boolean,
): {type: string; data: ReturnChangeAddressDataType} {
    return {
        type: 'CHANGE_CURRENT_SHIPPING_ADDRESS_MODE',
        data: {editable},
    };
}

/**
 * Change current billing address mode
 * @param {boolean} editable Editable
 * @return {{type: string, data: ReturnChangeAddressDataType}} New address mode
 */
export function changeCurrentBillingAddressMode(
    editable: boolean,
): {type: string; data: ReturnChangeAddressDataType} {
    return {
        type: 'CHANGE_CURRENT_BILLING_ADDRESS_MODE',
        data: {editable},
    };
}

type ReturnErrorAddressDataType = {
    errors: {
        [key: string]: string;
    };
};

/**
 * Set current shipping errors for address
 * @param {object} errors Editable
 * @return {{type: string, data: ReturnErrorAddressDataType}} New address errors
 */
export function setCurrentShippingAddressErrors(errors: {
    [key: string]: string;
}): {type: string; data: ReturnErrorAddressDataType} {
    return {
        type: 'SET_CURRENT_SHIPPING_ADDRESS_ERRORS',
        data: {errors},
    };
}

/**
 * Set current billing errors for address
 * @param {object} errors Editable
 * @return {{type: string, data: ReturnErrorAddressDataType}} New address errors
 */
export function setCurrentBillingAddressErrors(errors: {
    [key: string]: string;
}): {type: string; data: ReturnErrorAddressDataType} {
    return {
        type: 'SET_CURRENT_BILLING_ADDRESS_ERRORS',
        data: {errors},
    };
}

/**
 * Set current step
 * @param {number} currentStep Checkout step
 * @return {{type: string, currentStep: number}} currentStep
 */
export function setCurrentStep(currentStep: number): {type: string; currentStep: number} {
    return {
        type: 'SET_CURRENT_STEP',
        currentStep,
    };
}

/**
 * Set payment methods counter for google analytics
 * @param {number} counterPayment methods counter
 * @return {{type: string, counterPayment: number}} counterPayment
 */
export function counterPaymentAdd(counterPayment: number): {type: string; counterPayment: number} {
    return {
        type: 'COUNTER_PAYMENT_ADD',
        counterPayment,
    };
}

/**
 * Save order
 * @param {PaymentType} paymentInfo - payment info
 * @param {string} formKey formKey
 * @param {boolean} isSubscribe - subscribe newsletter
 * @return {ActionType<AxiosPromise>} Action
 */
export function saveOrder(
    paymentInfo: PaymentType,
    formKey: string,
    isSubscribe: boolean,
): ActionType<AxiosPromise> {
    const data = new FormData();
    const affid = getAffId();

    data.append('payment[method]', paymentInfo.code);
    data.append('payment[cc_type]', paymentInfo.type2);
    data.append('payment[cc_number]', paymentInfo.cardNumber.replace(/-/g, ''));
    data.append('payment[cc_exp_month]', paymentInfo.month);
    data.append('payment[cc_exp_year]', `20${paymentInfo.year}`);
    data.append('payment[cc_cid]', paymentInfo.cvv);

    data.append('form_key', formKey);
    data.append('agreement[1]', '1');
    data.append('subscribe-newsletter', `${Number(isSubscribe)}`);
    data.append('client_id', getGaClientId());
    data.append('ga_cid', getGaClientId());
    data.append('affid', affid);

    switch (paymentInfo.code) {
        case PAYMENT_METHODS.RECURLY:
            data.append('payment[token]', paymentInfo.paymentMethodToken);
            break;

        case PAYMENTS_METHODS.METHOD_KLARNA:
            data.append('payment[token]', paymentInfo.paymentMethodToken);
            data.append('payment[session_id]', paymentInfo.sessionId);
            break;

        default:
            break;
    }

    return {
        type: CHECKOUT.SAVE_ORDER,
        payload: instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.CHECKOUT_PLACE_ORDER,
            data,
        }),
    };
}

/**
 * Save order payment os
 * @param {PaymentType} paymentInfo - payment info
 * @param {string} formKey formKey
 * @param {boolean} isSubscribe - subscribe newsletter
 * @return {ActionType<AxiosPromise>} Action
 */
export function saveOrderPaymentOS(
    paymentInfo: PaymentType,
    formKey: string,
    isSubscribe: boolean,
): ActionType<AxiosPromise> {
    const data = new FormData();
    const affid = getAffId();
    data.append('form_key', formKey);

    data.append('payment[method]', paymentInfo.code);
    data.append('payment[token]', paymentInfo.paymentMethodToken);
    data.append('payment[encrypted_cvv]', paymentInfo.cvv);

    data.append('agreement[1]', '1');
    data.append('subscribe-newsletter', `${Number(isSubscribe)}`);
    data.append('client_id', getGaClientId());
    data.append('ga_cid', getGaClientId());
    data.append('affid', affid);

    return {
        type: CHECKOUT.SAVE_ORDER,
        payload: instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: ENDPOINTS.CHECKOUT_PLACE_ORDER,
            data,
        }),
    };
}

/**
 * Fetch an unauthorized customer addresses
 * @return {ActionType<AxiosPromise>} Action
 */
export function fetchAddressesUnauthorizedCustomer(): ActionType<AxiosPromise> {
    return {
        type: 'FETCH_ADDRESSES_UNAUTHORIZED_CUSTOMER',
        payload: instanceGusaBackend({
            method: 'get',
            url: ENDPOINTS.ADDRESSES_GET_UNAUTHORIZED,
        }),
    };
}
